import { LineString } from "ol/geom";
import GeometryLayout from "ol/geom/GeometryLayout";
import GeometryType from "ol/geom/GeometryType";
import { Draw } from "ol/interaction";
import { unByKey } from "ol/Observable";
import { getLength } from "ol/sphere";
import { Fill, Stroke, Style } from "ol/style";
import CircleStyle from "ol/style/Circle";
import { Switchboard } from "../../lib/events";
import { BaseInteraction } from "../../lib/interaction/interactions";
import { Maybe, None, Some } from "../../lib/misc/option";
import { ToolMessage } from "../messages/ToolMessage";
import { Tooltip } from "./tooltip/Tooltip";
export class LengthInteraction extends BaseInteraction {
    constructor(map, layer) {
        super();
        this.map = map;
        this.layer = layer;
        this.active = None.instance();
        this.listener = None.instance();
        this.name = "length";
        this.broadcaster = Switchboard.get_emitter("INTERACTION");
        this.emitter = Switchboard.get_emitter("CONTEXT");
        this.enter_context = new Some("toolbar");
        this.exit_context = new Some("revert");
    }
    init() {
        super.init();
        let draw = this.create_draw();
        this.attach_abort_handler(draw);
        this.interactions.push(draw);
        new ToolMessage({ selected: true, tool: this.name, command: "TOOL SELECTION" }).send();
    }
    remove() {
        this.remove_abort_handler();
        this.active = None.instance();
        this.listener = None.instance();
        super.remove();
        new ToolMessage({ selected: false, tool: this.name, command: "TOOL SELECTION" }).send();
    }
    create_draw() {
        const draw = new Draw({
            source: this.layer.source,
            type: GeometryType.LINE_STRING,
            style: new Style({
                fill: new Fill({
                    color: 'rgba(255, 255, 255, 0.6)'
                }),
                stroke: new Stroke({
                    color: 'rgba(255, 204, 51, 0.5)',
                    lineDash: [10, 10],
                    width: 2
                }),
                image: new CircleStyle({
                    radius: 5,
                    stroke: new Stroke({
                        color: 'rgba(255, 204, 51, 0.7)'
                    }),
                    fill: new Fill({
                        color: 'rgba(255, 255, 255, 0.6)'
                    })
                })
            })
        });
        draw.on('drawstart', this.draw_start_handler.bind(this));
        draw.on('drawend', this.draw_end_handler.bind(this));
        draw.on('drawabort', () => {
            this.cleanup_after_abort();
        });
        return draw;
    }
    draw_start_handler(evt) {
        let sketch = evt.feature;
        let tooltip = new Tooltip(sketch);
        this.active = new Some(tooltip);
        this.layer.add_tooltip(tooltip);
        this.map.addOverlay(tooltip.overlay);
        const geo = Maybe(sketch.getGeometry());
        geo.map(geom => {
            const listener = geom.on('change', (evt) => {
                let geometry = evt.target;
                let interior = geometry.getFlatMidpoint();
                let output = this.format_length(geometry, this.layer.projection);
                tooltip.update_position(interior);
                tooltip.update_text(output);
            });
            this.listener = new Some(listener);
        });
    }
    draw_end_handler(evt) {
        let feature = evt.feature;
        let geometry = feature.getGeometry();
        let coordinates = [];
        let midPoints = [];
        geometry.getCoordinates().forEach(c => {
            coordinates.push(c);
        });
        if (coordinates.length < 3) {
            this.active.map(tt => tt.make_static(this.layer));
            this.remove_listeners();
            return;
        }
        for (let i = 0; i < coordinates.length - 1; i++) {
            const startPoint = coordinates[i];
            const endPoint = coordinates[i + 1];
            const lineString = new LineString([...startPoint, ...endPoint], GeometryLayout.XY);
            midPoints.push(lineString.getFlatMidpoint());
            let tooltip = new Tooltip(feature);
            this.layer.add_tooltip(tooltip);
            this.map.addOverlay(tooltip.overlay);
            tooltip.overlay.setOffset([0, 0]);
            tooltip.update_position(lineString.getFlatMidpoint());
            let output = this.format_length(lineString, this.layer.projection);
            tooltip.update_text(output);
        }
        const startPoint = midPoints[0];
        const endPoint = midPoints[midPoints.length - 1];
        const lineString = new LineString([...startPoint, ...endPoint], GeometryLayout.XY);
        this.active.map(tt => { tt.update_position(lineString.getFlatMidpoint()); tt.overlay.setOffset([0, 0]); });
        this.remove_listeners();
    }
    format_length(line, projection) {
        const length = getLength(line, { projection: projection });
        const length_ft = length / 0.3048;
        if (length_ft > 5280)
            return this.format_number_string(Math.round(length_ft / 5280 * 100) / 100) + ' mi';
        return this.format_number_string(Math.round(length_ft * 100) / 100) + " ft";
    }
    attach_abort_handler(draw) {
        $(window).on('keyup', (evt) => {
            if (evt.key == "Escape") {
                draw.abortDrawing();
                this.cleanup_after_abort();
            }
        });
    }
    remove_abort_handler() {
        $(window).off('keyup');
    }
    cleanup_after_abort() {
        if (this.active.isSome()) {
            this.active.unwrap().remove_from(this.layer);
        }
        this.remove_abort_handler();
        this.broadcaster.emit('i_request', '');
        this.emitter.emit('ctx_request', 'singleton');
    }
    remove_listeners() {
        this.active = None.instance();
        this.listener.map(l => unByKey(l));
        this.listener = None.instance();
    }
}
